import { useState } from "react";
import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { SECTOARE_NIS2 } from "@/data/risk-sectors";
import type { Sector } from "@/types/risk-analysis";
import { normalizeSectorName } from "@/utils/sectorUtils";
import SectorCard from "./SectorCard";

const ANNEX_I = SECTOARE_NIS2.filter((s) => s.annex === "I");
const ANNEX_II = SECTOARE_NIS2.filter((s) => s.annex === "II");
const NORMALIZED_SECTOR_NAMES = new Set(
  SECTOARE_NIS2.map((s) => normalizeSectorName(s.nume))
);

type SectorPickerProps = {
  items: string[];
  onItemsChange: (items: string[]) => void;
  className?: string;
};

function filterSectors(sectors: Sector[], query: string): Sector[] {
  if (!query) return sectors;
  const q = normalizeSectorName(query);
  if (!q) return sectors;
  return sectors.filter(
    (s) =>
      normalizeSectorName(s.nume).includes(q) ||
      normalizeSectorName(s.subsectoare).includes(q)
  );
}

export default function SectorPicker({
  items,
  onItemsChange,
  className = "",
}: SectorPickerProps) {
  const [searchQuery, setSearchQuery] = useState("");

  const filteredI = filterSectors(ANNEX_I, searchQuery);
  const filteredII = filterSectors(ANNEX_II, searchQuery);
  const noResults = filteredI.length === 0 && filteredII.length === 0;

  const customSectors = items.filter(
    (name) => !NORMALIZED_SECTOR_NAMES.has(normalizeSectorName(name))
  );

  const handleToggle = (sectorName: string) => {
    if (
      items.some(
        (item) => normalizeSectorName(item) === normalizeSectorName(sectorName)
      )
    ) {
      onItemsChange(items.filter((s) => s !== sectorName));
    } else {
      onItemsChange([...items, sectorName]);
    }
  };

  const handleRemoveCustom = (sectorName: string) => {
    onItemsChange(items.filter((s) => s !== sectorName));
  };

  const selectedCount = items.length;

  return (
    <div className={className}>
      <div className="flex items-center gap-2 mb-3">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Caută sector..."
            className="pl-8 text-xs md:text-sm h-9"
          />
        </div>
        {selectedCount > 0 && (
          <Badge variant="secondary" className="shrink-0 text-xs tabular-nums">
            {selectedCount}/{SECTOARE_NIS2.length}
          </Badge>
        )}
      </div>

      {noResults && (
        <p className="py-6 text-center text-sm text-slate-400">
          Niciun sector găsit
        </p>
      )}

      {filteredI.length > 0 && (
        <div className="mb-4">
          <div className="mb-2 flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-red-400" />
            <h3 className="font-heading text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              Anexa I — Criticalitate Înaltă
            </h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {filteredI.map((sector) => (
              <SectorCard
                key={sector.id}
                sector={sector}
                isSelected={items.some(
                  (item) =>
                    normalizeSectorName(item) ===
                    normalizeSectorName(sector.nume)
                )}
                onToggle={handleToggle}
              />
            ))}
          </div>
        </div>
      )}

      {filteredII.length > 0 && (
        <div>
          <div className="mb-2 flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-amber-400" />
            <h3 className="font-heading text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              Anexa II — Alte Sectoare Critice
            </h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {filteredII.map((sector) => (
              <SectorCard
                key={sector.id}
                sector={sector}
                isSelected={items.some(
                  (item) =>
                    normalizeSectorName(item) ===
                    normalizeSectorName(sector.nume)
                )}
                onToggle={handleToggle}
              />
            ))}
          </div>
        </div>
      )}

      {customSectors.length > 0 && (
        <div className="mt-4 pt-3 border-t border-slate-100">
          <h3 className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">
            Sectoare personalizate
          </h3>
          <div className="flex flex-wrap gap-2">
            {customSectors.map((name) => (
              <Badge key={name} variant="secondary" className="gap-1 pr-1">
                {name}
                <button
                  type="button"
                  onClick={() => handleRemoveCustom(name)}
                  className="ml-1 rounded-full hover:bg-slate-300/50 p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
